import type {
	AssetSectionsMap,
	IncomeSectionMap,
	LiabilitySectionMap,
} from "@loanlink-nl/model-app";
import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useRepeatableCategory } from "@/composables/inventarisation/useRepeatableCategory";
import { useDossierDataStore } from "./useDossierDataStore";
import type { DeepPartial } from "@loanlink-nl/shared";

/**
 * Instanciates logic and functionality for a cofirm modal.
 */
const useConfirmDialog = () => {
	let callback = () => {};
	const isVisible = ref(false);

	interface DialogContent {
		heading: string;
		description?: string;
	}

	const content = ref<DialogContent>({
		heading: "Confirm action",
		description: "Are you sure you want to perform this action?",
	});

	const onConfirm = () => {
		isVisible.value = false;
		callback();
	};

	const onCancel = () => {
		isVisible.value = false;
	};

	/**
	 * Shows the confirmation modal.
	 * Callback is triggered on affirmative action.
	 */
	const confirm = (
		heading: string,
		description: string | undefined,
		cb: () => void,
	) => {
		isVisible.value = true;
		content.value.heading = heading;
		content.value.description = description;
		callback = cb;
	};

	return { isVisible, content, onConfirm, onCancel, confirm };
};

/**
 * General data store for inventarisation section of the app.
 */
export const useInventarisationStore = defineStore("inventarisation", () => {
	const dialog = useConfirmDialog();

	const dossierDataStore = useDossierDataStore();
	const { data } = storeToRefs(dossierDataStore);

	// Use computed to maintain reactivity when data.value is replaced
	const customers = computed(() => data.value.customers);

	const selectedCustomerId = ref<string | undefined>();

	function $reset() {
		selectedCustomerId.value = undefined;
	}

	// Pass getter functions to useRepeatableCategory so it re-evaluates
	// when data.value changes (e.g., from Zero sync or setData)
	const assets = useRepeatableCategory<DeepPartial<AssetSectionsMap>>(
		() => data.value.assets.byCustomer,
		() => ({
			investmentAccount: [],
			other: [],
			savingsAccount: [],
			savingsPlan: [],
		}),
	);

	const liabilities = useRepeatableCategory<DeepPartial<LiabilitySectionMap>>(
		() => data.value.liabilities.byCustomer,
		() => ({
			overdraftFacility: [],
			personalLoan: [],
			privateLease: [],
			revolvingCredit: [],
			spousalAlimony: [],
			studentDebtSF15: [],
			studentDebtSF35: [],
		}),
	);

	const income = useRepeatableCategory<DeepPartial<IncomeSectionMap>>(
		() => data.value.income.byCustomer,
		() => ({
			loondienst: [],
			uitkering: [],
			pensioen: [],
			onderneming: [],
			overig: [],
		}),
	);

	return {
		customers,
		dialog,
		assets,
		liabilities,
		income,
		selectedCustomerId,

		$reset,
	};
});
